import React from "react";
import { findById, GroupUnits, IngredientGroup } from "../../utils/planUtils";
import { formatPurchaseDescription } from "../../utils/purchaseUtils";
import { usePersonsAsync } from "../../queries/queries";
import {
  useFoodAsync,
  useFoodByUnitAsync,
  usePurchaseAsync,
  usePurchasesByFoodAsync,
} from "../../queries/caches";
import { AmountUnit, PurchaseUnitAmount, UnitAmount } from "../UnitAmount";
import { SmallIconButton } from "../SmallIconButton";
import { IngredientRow } from "../dish/IngredientRow";
import { FoodDto } from "../../api";
import { emptyNutriValue, NutriValues } from "../../data/nutriValues";

export function StepIngredientCard(props: {
  group: IngredientGroup;
  currentTotalAmount: number;
  units: GroupUnits;
  currentPersonAmounts: Record<number, number>;
  captured: number | null | undefined;
  isLast?: boolean;
  onCardClick: () => void;
  onTotalAmountChange: (newTotal: number) => void;
  onTotalUnitChange: (u: AmountUnit | undefined) => void;
  onPersonAmountChange: (personId: number, amount: number) => void;
  onSplitValues: () => void;
  onDeleteGroup: () => void;
  onSelectAlternative: (food: FoodDto) => void;
}) {
  const { group, captured, units } = props;
  const isCaptured = captured !== undefined;
  const persons = usePersonsAsync(false);
  const firstMi = group.pairs[0].mi;

  const capturedPurchase = usePurchaseAsync(captured ?? undefined);
  const planFood = useFoodAsync(
    group.isFoodBased ? firstMi.foodUnitFoodId : undefined,
  );
  const foodByUnit = useFoodByUnitAsync(
    group.isFoodBased && units.kind === "food" ? units.current?.id : undefined,
  );
  const capturedPurchaseFood = useFoodAsync(capturedPurchase?.foodId);
  const activeFood =
    group.isFoodBased && units.kind === "food"
      ? foodByUnit ?? planFood
      : capturedPurchaseFood;
  const foodPurchases = usePurchasesByFoodAsync(activeFood?.id);
  const selectable = foodPurchases.filter((p) => p.purchaseRelevant).length > 1;

  const mainFoodUnitId = group.recipeIngredient?.foodUnitId;
  const alternatives = group.recipeIngredient?.alternatives || [];
  const options = [
    ...(mainFoodUnitId ? [mainFoodUnitId] : []),
    ...(alternatives.map((alt) => alt.foodUnitId).filter(Boolean) as number[]),
  ];

  const displayName =
    (!group.isFoodBased || isCaptured) && capturedPurchase
      ? formatPurchaseDescription(capturedPurchase)
      : activeFood?.description || group.name;

  const nutri: NutriValues = (() => {
    const amount = props.currentTotalAmount;
    if (amount <= 0) return { ...emptyNutriValue };
    if (units.kind === "purchase") {
      const pu = units.current;
      if (!capturedPurchase || !pu) return { ...emptyNutriValue };
      const factor = (pu.netWeight != null ? pu.netWeight / 100 : 1) * amount;
      return {
        fat: capturedPurchase.fat * factor,
        carbs: capturedPurchase.carbs * factor,
        protein: capturedPurchase.protein * factor,
        kcal: (capturedPurchase.kcal ?? 0) * factor,
      };
    }
    const fu = units.current;
    if (!activeFood || !fu) return { ...emptyNutriValue };
    const factor = (fu.netWeight != null ? fu.netWeight / 100 : 1) * amount;
    return {
      fat: activeFood.fat * factor,
      carbs: activeFood.carbs * factor,
      protein: activeFood.protein * factor,
      kcal: (activeFood.kcal ?? 0) * factor,
    };
  })();

  const personRows = (
    <div className="flex flex-col gap-2 border-t border-line/40 pt-3">
      {group.pairs.map((p) => {
        const person = findById(persons, p.meal.meal.personId);
        const amt = props.currentPersonAmounts[p.meal.meal.personId] ?? 0;
        return (
          <div
            key={p.meal.meal.personId}
            className="flex items-center justify-between gap-2"
          >
            <span className="w-12 truncate text-sm text-ink">
              {person?.name}
            </span>
            <div className="min-w-0 flex-1">
              {units.kind === "purchase" ? (
                <PurchaseUnitAmount
                  purchaseUnit={units.current}
                  purchaseUnits={units.current ? [units.current] : []}
                  amount={amt}
                  onUnitChange={() => {}}
                  onAmountChange={(a) =>
                    props.onPersonAmountChange(p.meal.meal.personId, a)
                  }
                />
              ) : (
                <UnitAmount
                  foodUnit={units.current}
                  units={units.current ? [units.current] : []}
                  amount={amt}
                  onUnitChange={() => {}}
                  onAmountChange={(a) =>
                    props.onPersonAmountChange(p.meal.meal.personId, a)
                  }
                />
              )}
            </div>
            <SmallIconButton
              label="Zutat löschen"
              icon="delete"
              variant="destructive"
              onClick={() => props.onPersonAmountChange(p.meal.meal.personId, 0)}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <IngredientRow
      dataCy="cooking-ingredient-row"
      isLast={props.isLast}
      title={displayName}
      alternativeUnitIds={options}
      activeFoodId={activeFood?.id}
      onSelectAlternative={(food) => props.onSelectAlternative(food)}
      amount={
        units.kind === "purchase" ? (
          <PurchaseUnitAmount
            purchaseUnit={units.current}
            purchaseUnits={units.selectable}
            amount={props.currentTotalAmount}
            onUnitChange={(u) => props.onTotalUnitChange(u)}
            onAmountChange={(a) => props.onTotalAmountChange(a)}
          />
        ) : (
          <UnitAmount
            foodUnit={units.current}
            units={units.selectable}
            amount={props.currentTotalAmount}
            onUnitChange={(u) => props.onTotalUnitChange(u)}
            onAmountChange={(a) => props.onTotalAmountChange(a)}
          />
        )
      }
      onDelete={props.onDeleteGroup}
      deleteLabel="Zutat löschen"
      trailing={
        <SmallIconButton
          label="Aufteilen"
          icon="splitValues"
          onClick={props.onSplitValues}
        />
      }
      extra={personRows}
      nutri={nutri}
      product={{
        highlighted: selectable && !isCaptured,
        onClick: props.onCardClick,
        dataCy: "cooking-product-button",
      }}
    />
  );
}
